import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Plane, Camera, ArrowRight, Calculator, Plus, TrendingUp, DollarSign, Info, Box, PlaneTakeoff } from 'lucide-react';

interface ROICalculatorPricingProps {
  showRoiCalculator?: boolean;
  currency?: 'usd' | 'eur';
  locale?: 'en' | 'fr';
  /** CTA des cartes (ex. « Try One ») : ex. `/partners/signup?section=try-one`. */
  signupPath?: string;
  /** Mode partenaires (FR & US) : tarifs réduits photographes Matterport. */
  partnerMode?: boolean;
}

const ROICalculatorPricing: React.FC<ROICalculatorPricingProps> = ({
  showRoiCalculator = true,
  currency = 'usd',
  locale = 'en',
  signupPath = '/signup',
  partnerMode = false,
}) => {
  const [activeTab, setActiveTab] = useState<'standard' | 'drone'>('standard');
  const [simulatorTab, setSimulatorTab] = useState<'standard' | 'drone'>('standard');
  const [monthlyVolume, setMonthlyVolume] = useState(6);
  /** Partenaires US : prix de revente par défaut affiché dans le simulateur. */
  const [resellPrice, setResellPrice] = useState(() => {
    if (partnerMode && currency === 'usd') return 175;
    if (partnerMode && currency === 'eur') return 170;
    return 150;
  });
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'professional' | 'enterprise'>('professional');

  const sym = currency === 'eur' ? '€' : '$';
  const isFR = locale === 'fr';
  const t = isFR ? {
    title: 'Tarifs simples et transparents',
    subtitle: 'Testez avec un crédit à l’unité, puis passez à la formule mensuelle adaptée à votre volume',
    standard: 'Standard',
    droneClips: '+ clips de drone',
    creditsInfo: 'Les crédits non utilisés restent valables 6 mois.',
    mostPopular: 'LE PLUS POPULAIRE',
    perMonth: '/mois',
    propertiesMonth: 'biens / mois',
    save5: 'Économisez 5 %',
    save6: 'Économisez 6 %',
    save7: 'Économisez 7 %',
    save8: 'Économisez 8 %',
    save10: 'Économisez 10 %',
    save12: 'Économisez 12 %',
    save9: 'Économisez 9 %',
    save13: 'Économisez 13 %',
    youGet: 'Inclus par bien :',
    deliverable: '1 vidéo professionnelle de visite virtuelle',
    walkthroughOption: '',
    delivery: 'Livraison 24 h (lun–jeu)',
    droneIncluded: 'Séquences drone incluses',
    buyExtra: 'Crédits à l\'unité disponibles',
    fridayOrders: 'Vendredi → livré lundi',
    getStarted: 'Tester pour une propriété',
    calcTitle: 'Calculez votre gain mensuel',
    calcSubtitle: partnerMode
      ? 'Estimez ce que vous pouvez gagner en ajoutant la vidéo à vos prestations'
      : 'Estimez le profit que vous pouvez générer en revendant ces vidéos.',
    profitSimulator: 'Choisissez votre formule et simulez votre gain mensuel',
    simulatorStandardTab: 'Standard',
    simulatorDroneTab: '+ clips de drone',
    selectPlan: partnerMode
      ? ''
      : 'Sélectionnez votre formule et entrez vos chiffres.',
    selectYourPlan: 'Choisir votre formule',
    propsPerMonth: 'Biens par mois',
    minIncluded: 'inclus',
    extraCredit: 'Crédit supplémentaire',
    resellPrice: 'Prix de revente à votre client',
    monthlyRevenue: partnerMode ? 'Vous facturez' : 'Chiffre d\'affaires mensuel',
    monthlyProfit: 'Votre profit mensuel',
    yearlyEarnings: 'Gains annuels',
    months: 'mois',
    property: 'bien',
    properties: 'biens',
    extra: 'supplémentaire',
    extras: 'supplémentaires',
    parBien: 'par bien',
    creditEqualsVideo: '1 crédit = 1 vidéo',
    creditsLineAboveCards: '1 crédit = 1 vidéo · Crédits valables 6 mois',
    creditsPerMonthSuffix: 'crédits/mois',
    creditUnit: 'crédits',
    propertiesPerMonthUnit: 'vidéos/mois',
    fromLabel: 'À partir de',
    perMonthShort: '/mois',
    perVideoUnit: '/ vidéo',
  } : {
    title: 'Simple, Transparent Pricing',
    subtitle: 'Try with a single credit, then switch to the monthly plan that fits your volume.',
    standard: 'Standard',
    droneClips: '+ drone clips',
    creditsInfo: "Unused credits don't roll over next month.",
    mostPopular: 'MOST POPULAR',
    perMonth: '/mo',
    propertiesMonth: 'properties / month',
    save5: 'Save 5%',
    save6: 'Save 6%',
    save7: 'Save 7%',
    save8: 'Save 8%',
    save10: 'Save 10%',
    save12: 'Save 12%',
    save9: 'Save 9%',
    save13: 'Save 13%',
    youGet: 'You get per property:',
    walkthroughOption: "Walkthrough Video (Branded) with client's logo",
    deliverable: '',
    delivery: '24-hour delivery (Mon-Thu)',
    droneIncluded: 'drone clips included',
    buyExtra: 'Additional credits available',
    fridayOrders: 'Friday → delivered Monday',
    getStarted: 'Try One Property',
    calcTitle: 'Calculate Your ROI',
    calcSubtitle: 'See how much profit you can generate by reselling these videos.',
    profitSimulator: 'Profit Simulator',
    simulatorStandardTab: 'Standard',
    simulatorDroneTab: '+ drone clips',
    selectPlan: 'Select your plan and enter your numbers.',
    selectYourPlan: 'Select your plan',
    propsPerMonth: 'Properties per month',
    minIncluded: 'included in plan',
    extraCredit: 'Extra credit',
    resellPrice: 'Your Resell Price to Client',
    monthlyRevenue: 'Monthly Revenue',
    monthlyProfit: 'Your Monthly Profit',
    yearlyEarnings: 'Yearly Earnings',
    months: 'months',
    property: 'property',
    properties: 'properties',
    extra: 'extra',
    extras: 'extras',
    parBien: 'per property',
    creditEqualsVideo: '1 credit = 1 video',
    creditsLineAboveCards: '1 credit = 1 video · Credits valid for 6 months',
    creditsPerMonthSuffix: 'credits/month',
    creditUnit: 'credits',
    propertiesPerMonthUnit: 'videos/month',
    fromLabel: 'From',
    perMonthShort: '/mo',
    perVideoUnit: '/ video',
  };

  const plans = isFR
    ? (partnerMode
      ? {
          // Partenaires FR – photographes Matterport
          standard: [
            { id: 'starter',      name: '3 vidéos/mois', price: 285,  perProperty: 95,  period: t.perMonth, properties: 3, savings: '',               popular: false },
            { id: 'professional', name: '6 vidéos/mois', price: 510,  perProperty: 85,  period: t.perMonth, properties: 6, savings: 'Économisez 11 %', popular: true  },
            { id: 'enterprise',   name: '9 vidéos/mois', price: 720,  perProperty: 80,  period: t.perMonth, properties: 9, savings: 'Économisez 16 %', popular: false },
          ],
          drone: [
            { id: 'starter',      name: '3 vidéos/mois', price: 405,  perProperty: 135, period: t.perMonth, properties: 3, savings: '',               popular: false },
            { id: 'professional', name: '6 vidéos/mois', price: 750,  perProperty: 125, period: t.perMonth, properties: 6, savings: 'Économisez 7 %',  popular: true  },
            { id: 'enterprise',   name: '9 vidéos/mois', price: 1080, perProperty: 120, period: t.perMonth, properties: 9, savings: 'Économisez 11 %', popular: false },
          ],
        }
      : {
          // Agences FR – abonnements mensuels
          standard: [
            { id: 'starter',      name: '3 vidéos/mois', price: 450,  perProperty: 150, period: t.perMonth, properties: 3, savings: '',               popular: false },
            { id: 'professional', name: '6 vidéos/mois', price: 840,  perProperty: 140, period: t.perMonth, properties: 6, savings: 'Économisez 7 %',  popular: true  },
            { id: 'enterprise',   name: '9 vidéos/mois', price: 1170, perProperty: 130, period: t.perMonth, properties: 9, savings: 'Économisez 13 %', popular: false },
          ],
          drone: [
            { id: 'starter',      name: '3 vidéos/mois', price: 570,  perProperty: 190, period: t.perMonth, properties: 3, savings: '',               popular: false },
            { id: 'professional', name: '6 vidéos/mois', price: 1080, perProperty: 180, period: t.perMonth, properties: 6, savings: 'Économisez 5 %',  popular: true  },
            { id: 'enterprise',   name: '9 vidéos/mois', price: 1530, perProperty: 170, period: t.perMonth, properties: 9, savings: 'Économisez 11 %', popular: false },
          ],
        })
    : (partnerMode
      ? {
          // Partners US – same numbers in $
          standard: [
            { id: 'starter',      name: '3 videos/month', price: 285,  perProperty: 95,  period: t.perMonth, properties: 3, savings: '',            popular: false },
            { id: 'professional', name: '6 videos/month', price: 510,  perProperty: 85,  period: t.perMonth, properties: 6, savings: 'Save 11%',     popular: true  },
            { id: 'enterprise',   name: '9 videos/month', price: 720,  perProperty: 80,  period: t.perMonth, properties: 9, savings: 'Save 16%',     popular: false },
          ],
          drone: [
            { id: 'starter',      name: '3 videos/month', price: 405,  perProperty: 135, period: t.perMonth, properties: 3, savings: '',            popular: false },
            { id: 'professional', name: '6 videos/month', price: 750,  perProperty: 125, period: t.perMonth, properties: 6, savings: 'Save 7%',      popular: true  },
            { id: 'enterprise',   name: '9 videos/month', price: 1080, perProperty: 120, period: t.perMonth, properties: 9, savings: 'Save 11%',     popular: false },
          ],
        }
      : {
          // US non-partner (si utilisé ailleurs) – on garde l’ancienne grille
          standard: [
            { id: 'starter', name: '3 videos/month', price: 255, perProperty: 85, period: t.perMonth, properties: 3, savings: '',        popular: false },
            { id: 'professional', name: '6 videos/month', price: 480, perProperty: 80, period: t.perMonth, properties: 6, savings: 'Save 6%', popular: true },
            { id: 'enterprise', name: '9 videos/month', price: 675, perProperty: 75, period: t.perMonth, properties: 9, savings: 'Save 12%', popular: false }
          ],
          drone: [
            { id: 'starter', name: '3 videos/month', price: 336, perProperty: 112, period: t.perMonth, properties: 3, savings: '',         popular: false },
            { id: 'professional', name: '6 videos/month', price: 630, perProperty: 105, period: t.perMonth, properties: 6, savings: 'Save 6%',  popular: true },
            { id: 'enterprise', name: '9 videos/month', price: 891, perProperty: 99, period: t.perMonth, properties: 9, savings: 'Save 12%',  popular: false }
          ],
        });

  const partnerStandardUnit = 95;
  const partnerDroneUnit = 135;

  const extraCreditPrice = partnerMode
    ? (simulatorTab === 'standard' ? partnerStandardUnit : partnerDroneUnit)
    : (simulatorTab === 'standard' ? 150 : 190);

  const getSelectedPlanData = () => {
    return plans[simulatorTab].find(p => p.id === selectedPlan) || plans[simulatorTab][1];
  };

  const minProperties = getSelectedPlanData().properties;

  useEffect(() => {
    if (partnerMode && currency === 'usd') {
      setResellPrice(175);
    } else if (partnerMode && currency === 'eur') {
      setResellPrice(simulatorTab === 'standard' ? 170 : 240);
    } else {
      setResellPrice(simulatorTab === 'standard' ? 150 : 190);
    }
  }, [simulatorTab, partnerMode, currency]);

  useEffect(() => {
    const plan = getSelectedPlanData();
    setMonthlyVolume(plan.properties);
  }, [selectedPlan, simulatorTab]);

  const handleVolumeChange = (value: number) => {
    if (value >= minProperties) {
      setMonthlyVolume(value);
    } else if (value > 0) {
      setMonthlyVolume(minProperties);
    }
  };

  const handleVolumeBlur = (value: number) => {
    if (value < minProperties || isNaN(value)) {
      setMonthlyVolume(minProperties);
    }
  };

  const calculateCosts = () => {
    const plan = getSelectedPlanData();
    const baseProperties = plan.properties;
    const planCost = plan.price;
    
    const extraUnitCost = partnerMode
      ? (simulatorTab === 'standard' ? partnerStandardUnit : partnerDroneUnit)
      : (simulatorTab === 'standard' ? 150 : 190);
    
    let totalCost = planCost;
    let extraProperties = 0;
    
    if (monthlyVolume > baseProperties) {
      extraProperties = monthlyVolume - baseProperties;
      totalCost += extraProperties * extraUnitCost;
    }
    
    const totalRevenue = (resellPrice || 0) * monthlyVolume;
    const profit = totalRevenue - totalCost;
    const yearlyProfit = profit * 12;
    
    return { totalCost, totalRevenue, profit, yearlyProfit, planCost, extraProperties, extraUnitCost, baseProperties };
  };

  const { totalRevenue, profit, yearlyProfit, extraProperties, extraUnitCost, planCost, baseProperties } = calculateCosts();

  const extraLabel = extraProperties > 1 ? t.extras : t.extra;

  const FeatureRow = ({ text, highlight = false, isExtra = false, useInfoIcon = false }: { text: string; highlight?: boolean; isExtra?: boolean; useInfoIcon?: boolean }) => {
    return (
      <div className="flex items-center justify-between gap-2 py-2 border-b border-gray-50 last:border-0 group">
        <div className="flex items-center min-w-0 flex-1">
          {useInfoIcon ? (
            <span className="mr-2 sm:mr-3 shrink-0"><Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" /></span>
          ) : (
            <div className={`p-1 rounded-full mr-2 sm:mr-3 shrink-0 ${isExtra ? 'bg-[#3C59B2]/10' : highlight ? 'bg-[#3C59B2]/10' : 'bg-green-50'}`}>
              {isExtra ? <Plus className="w-3 h-3 text-[#3C59B2]" /> : highlight ? <Plane className="w-3 h-3 text-[#3C59B2]" /> : <Check className="w-3 h-3 text-green-500" />}
            </div>
          )}
          <span className={`text-xs sm:text-sm whitespace-pre-line ${isExtra ? 'font-medium text-[#3C59B2]' : highlight ? 'font-medium text-[#3C59B2]' : 'text-gray-600'}`}>{text}</span>
        </div>
      </div>
    );
  };

  return (
    <section id="pricing" className="py-12 sm:py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12 md:mb-16">
          <motion.h2
            className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {t.title}
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg md:text-xl text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {t.subtitle}
          </motion.p>
          
          <div className="mt-6 sm:mt-8 flex flex-row gap-3 justify-center items-center">
            <button onClick={() => setActiveTab('standard')} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${activeTab === 'standard' ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
              <span className="flex items-center gap-2"><Box className="w-4 h-4" />{t.standard}</span>
            </button>
            <button onClick={() => setActiveTab('drone')} className={`btn-premium overflow-hidden whitespace-nowrap px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${activeTab === 'drone' ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
              <span className="flex items-center gap-2"><PlaneTakeoff className="w-4 h-4" />{t.droneClips}</span>
            </button>
          </div>

          {/* Info message */}
          <div className="mt-6 flex justify-center">
            <div className="inline-flex items-center gap-3 bg-white border border-[#E2E8F0] px-5 py-2.5 rounded-full shadow-sm">
              <div className="w-5 h-5 rounded-full bg-[#3C59B2] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">i</span>
              </div>
              <span className="text-sm text-gray-600">{t.creditsLineAboveCards}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-16 md:mb-24">
          {plans[activeTab].map((plan, index) => (
            <motion.div
              key={index}
              className={`card-hover bg-white rounded-3xl p-5 sm:p-6 md:p-8 border transition-all duration-300 relative flex flex-col ${plan.popular ? 'border-[#3C59B2] shadow-xl z-10 md:-translate-y-4' : 'border-[#E2E8F0]'}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {plan.savings && (<div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-700 px-4 sm:px-6 py-1.5 rounded-full text-[10px] sm:text-xs font-bold shadow-md whitespace-nowrap">{plan.savings}</div>)}
              <div className="text-center mb-6">
                <h3 className="font-manrope text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3">{plan.properties} {t.propertiesPerMonthUnit}</h3>
                {/* Prix par propriété en gros, prix total abonnement en petit */}
                <div className={`text-3xl sm:text-4xl font-bold ${plan.popular ? 'text-[#3C59B2]' : 'text-slate-900'}`}>{sym}{plan.perProperty}{isFR ? ` ${t.parBien}` : ''}</div>
                {!isFR && <div className="text-sm text-slate-500 mt-1">{t.parBien}</div>}
                <div className="text-sm text-slate-500 mt-2"><span className="font-semibold text-slate-600">{sym}{plan.price}{t.perMonthShort}</span></div>
              </div>
              <div className="flex-1 space-y-2 mb-6 sm:mb-8 border-t border-gray-100 pt-4 sm:pt-6">
                {isFR ? null : (t.walkthroughOption ? <FeatureRow text={t.walkthroughOption} /> : null)}
                {isFR ? null : <FeatureRow text="Unbranded version included (MLS-ready)" />}
                <FeatureRow text={`${plan.properties} ${t.creditsPerMonthSuffix}`} />
                <FeatureRow text={t.delivery} />
                {activeTab === 'drone' && <FeatureRow text={t.droneIncluded} highlight={true} />}
                <FeatureRow text={t.buyExtra} isExtra={true} />
                <FeatureRow text={t.fridayOrders} useInfoIcon />
              </div>
              <a href={signupPath} className={`btn-premium overflow-hidden w-full py-3 sm:py-4 rounded-full font-bold text-center transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${plan.popular ? 'bg-[#3C59B2] text-white hover:bg-[#2d4a8f]' : 'bg-white text-[#3C59B2] border-2 border-[#3C59B2] hover:bg-slate-50'}`}>{t.getStarted} <ArrowRight className="w-4 h-4" /></a>
            </motion.div>
          ))}
        </div>

        {showRoiCalculator && (
        <motion.div
          id="roi-calculator"
          className="max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-6 sm:mb-8 md:mb-12">
            <h3 className="font-manrope text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">{t.calcTitle}</h3>
            <p className="text-gray-600 mt-2 text-base sm:text-lg md:text-xl">{t.calcSubtitle}</p>
          </div>
          <div className="card-hover bg-white rounded-3xl border border-[#E2E8F0] overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 lg:p-10 bg-white border-b border-[#E2E8F0]">
              <div className="flex flex-col gap-4">
                <div className="text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-[#3C59B2]" />
                    <h3 className="font-manrope text-lg sm:text-xl md:text-2xl font-bold text-[#3C59B2]">{t.profitSimulator}</h3>
                  </div>
                  <p className="text-gray-600 font-medium text-xs sm:text-sm md:text-base">{t.selectPlan}</p>
                </div>
                <div className="flex justify-center sm:justify-start">
                  <div className="inline-flex bg-white p-1 rounded-full shadow-sm border border-gray-200">
                    <button onClick={() => setSimulatorTab('standard')} className={`btn-premium overflow-hidden px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[11px] sm:text-xs font-bold transition-all ${simulatorTab === 'standard' ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                      <div className="flex items-center gap-1 sm:gap-1.5"><Box className="w-3 h-3 sm:w-3.5 sm:h-3.5" /><span>{t.simulatorStandardTab}</span></div>
                    </button>
                    <button onClick={() => setSimulatorTab('drone')} className={`btn-premium overflow-hidden px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-[11px] sm:text-xs font-bold transition-all ${simulatorTab === 'drone' ? 'bg-[#3C59B2] text-white' : 'bg-white border-2 border-[#3C59B2] text-[#3C59B2] hover:bg-slate-50'}`}>
                      <div className="flex items-center gap-1 sm:gap-1.5"><PlaneTakeoff className="w-3 h-3 sm:w-3.5 sm:h-3.5" /><span>{t.simulatorDroneTab}</span></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 p-4 sm:p-6 md:p-8 lg:p-10">
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label className="font-bold text-gray-700 text-sm sm:text-base block mb-2 sm:mb-3">{t.selectYourPlan}</label>
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                    {plans[simulatorTab].map((plan) => (
                      <button
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id as 'starter' | 'professional' | 'enterprise')}
                        className={`btn-premium overflow-hidden p-2 sm:p-3 rounded-full border-2 transition-all text-center ${
                          selectedPlan === plan.id
                            ? 'border-[#3C59B2] bg-[#3C59B2]/5'
                            : 'border-[#E2E8F0] hover:border-[#3C59B2]'
                        }`}
                      >
                        <p className={`text-[10px] sm:text-xs font-bold ${selectedPlan === plan.id ? 'text-[#3C59B2]' : 'text-gray-600'}`}>{plan.properties} {t.propertiesPerMonthUnit}</p>
                        <p className={`text-base sm:text-lg font-extrabold ${selectedPlan === plan.id ? 'text-[#3C59B2]' : 'text-gray-900'}`}>{sym}{plan.price}</p>
                        <p className="text-[9px] sm:text-[10px] text-gray-500 font-medium">{sym}{plan.perProperty}{t.perVideoUnit}</p>
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-gray-500">
                    <Plus className="w-3 h-3 text-[#3C59B2]" />
                    <span>{t.extraCredit}: <span className="font-bold text-[#3C59B2]">{sym}{extraCreditPrice}</span> / {t.property}</span>
                  </div>
                </div>

                <div>
                  <label className="font-bold text-gray-700 text-sm sm:text-base block mb-2">{t.propsPerMonth}</label>
                  <input 
                    type="number" 
                    min={minProperties}
                    max="100" 
                    value={monthlyVolume || ''} 
                    onChange={(e) => handleVolumeChange(parseInt(e.target.value) || 0)}
                    onBlur={(e) => handleVolumeBlur(parseInt(e.target.value))}
                    className="w-full h-12 sm:h-14 text-xl sm:text-2xl font-bold text-[#3C59B2] text-center border-2 border-gray-200 rounded-xl focus:border-[#3C59B2] focus:outline-none transition-colors"
                  />
                  <p className="text-[10px] sm:text-xs text-gray-400 mt-2 text-center">
                    {extraProperties > 0 
                      ? <span className="text-gray-500">{baseProperties} {isFR ? 'inclus +' : 'included +'} <span className="text-[#3C59B2] font-medium">{extraProperties} {extraLabel}</span> ({sym}{extraUnitCost}/{isFR ? 'chacun' : 'each'})</span>
                      : <span>{isFR ? 'Minimum\u00a0:' : 'Minimum:'} {minProperties} {t.properties} ({t.minIncluded})</span>
                    }
                  </p>
                </div>

                <div>
                  <label className="font-bold text-gray-700 text-sm sm:text-base block mb-2">{t.resellPrice}</label>
                  <div className="relative">
                    <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-xl sm:text-2xl font-bold text-green-600">{sym}</span>
                    <input 
                      type="number" 
                      min="1" 
                      max="2000" 
                      step="10"
                      value={resellPrice || ''} 
                      onChange={(e) => setResellPrice(parseInt(e.target.value) || 0)}
                      onBlur={(e) => setResellPrice(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full h-12 sm:h-14 text-xl sm:text-2xl font-bold text-green-600 text-center border-2 border-gray-200 rounded-xl focus:border-green-500 focus:outline-none transition-colors pl-8"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <div className="bg-gray-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                    <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-gray-500">{t.monthlyRevenue}</p>
                  </div>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-800">{sym}{totalRevenue.toLocaleString()}</p>
                  <p className="text-[9px] sm:text-[10px] text-gray-400 italic mt-1">{monthlyVolume} {monthlyVolume > 1 ? t.properties : t.property} × {sym}{resellPrice}</p>
                </div>

                <div className="bg-green-600 rounded-2xl p-4 sm:p-5 text-white text-center">
                  <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wide opacity-90">{t.monthlyProfit}</p>
                  <p className="text-3xl sm:text-4xl md:text-5xl font-extrabold">+{sym}{profit.toLocaleString()}</p>
                  <p className="text-[9px] sm:text-[10px] opacity-80 italic mt-1">
                    {extraProperties > 0 
                      ? `${sym}${totalRevenue.toLocaleString()} - ${sym}${planCost} ${isFR ? 'formule' : 'plan'} - ${extraProperties} × ${sym}${extraUnitCost} ${extraLabel}`
                      : `${sym}${totalRevenue.toLocaleString()} - ${sym}${planCost} ${isFR ? 'formule' : 'plan'}`
                    }
                  </p>
                </div>

                <div className="bg-[#3C59B2] rounded-xl sm:rounded-2xl p-3 sm:p-4 text-white text-center">
                  <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wide opacity-90">{t.yearlyEarnings}</p>
                  </div>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold">{sym}{yearlyProfit.toLocaleString()}</p>
                  <p className="text-[9px] sm:text-[10px] opacity-80 italic mt-1">{sym}{profit.toLocaleString()} × 12 {t.months}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        )}
      </div>
    </section>
  );
};

export default ROICalculatorPricing;